import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Typography, Box, Grid, List, ListItem, ListItemText, Button, Alert, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarIcon from '@mui/icons-material/Star';
import { fetchCourseDetails, saveCourseDetails, updateUserEnrollment } from '../config/firestoreConfig';
import { useAuth } from '../context/AuthContext'; 
import LoadingSpinner from '../components/LoadingSpinner';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const CourseDetailsPage = () => {
  const { courseId } = useParams();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEnrolling, setIsEnrolling] = useState(false); 
  const [isAlreadyEnrolled, setIsAlreadyEnrolled] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null); 
  const [applyUrlClickedCount, setApplyUrlClickedCount] = useState(0); 
  const [courseEnrolledCount, setCourseEnrolledCount] = useState(0);



  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Check if it's mobile
  const isTablet = useMediaQuery(theme.breakpoints.down('md')); // Check if it's tablet

  useEffect(() => {
    const getCourseData = async () => {
      const courseData = await fetchCourseDetails(courseId);
      setCourse(courseData);

      // Check if the user is already enrolled
      if (user && courseData?.enrolled_persons) {
        const alreadyEnrolled = courseData.enrolled_persons.some(
          (enrollment) => enrollment.mailid === user.email
        );
        setIsAlreadyEnrolled(alreadyEnrolled);
      }

      setIsLoading(false);

      // Count the unique emails in apply_url_clicked
      if (courseData?.apply_url_clicked) {
        const uniqueEmailCount = new Set(courseData.apply_url_clicked).size;
        setApplyUrlClickedCount(uniqueEmailCount);
      }

      if (courseData?.enrolled_persons) {
        const uniqueEmailCount = new Set(courseData.enrolled_persons).size;
        setCourseEnrolledCount(uniqueEmailCount);
      }
    };
    getCourseData();
  }, [courseId, user]);

  const handleEnroll = async () => {
    if (!user) {
      // If user is not logged in, show an alert
      setErrorMessage("You must be logged in to enroll in the course!");
      return;
    }

    setIsEnrolling(true); // Start enrollment progress
    const enrollTime = new Date().toISOString();

    // Create a new enrollment object for the course
    const newEnrollment = {
      mailid: user.email,
      date: enrollTime,
    };

    // Add this user to the enrolled persons array in the course document
    const updatedCourse = {
      ...course,
      enrolled_persons: course.enrolled_persons
        ? [...course.enrolled_persons, newEnrollment]
        : [newEnrollment], // If no enrolled persons, initialize array with the first entry
    };

    // Save updated course data
    try {
      await saveCourseDetails(courseId, updatedCourse);

      // Update the user’s enrolled courses in the "Valarchi users" collection
      const userEnrollment = {
        id: courseId,
        date: enrollTime,
        progress: 0, // Default progress value
        current_page: 0,
        title: course.title,
      };
      await updateUserEnrollment(user.email, userEnrollment); // Update the user's enrolled courses

      // Mark as enrolled
      setIsAlreadyEnrolled(true);
      setErrorMessage(null); // Clear any previous errors
      alert('You have successfully enrolled in the course!');
    } catch (error) {
      console.error("Error saving course details:", error);
      setErrorMessage('Failed to enroll. Please try again later.');
    } finally {
      setIsEnrolling(false); // Stop enrollment progress
    }
  };

  const handleOpenCourse = () => {
    navigate(`/courses/${courseId}/content`); // Navigate to course content page
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!course) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h6" color="error" align="center">
          Course not found! Please check the course ID or try again later.
        </Typography>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: isMobile ? 4 : 6, // Adjust padding for mobile devices
        mt: isMobile ? 4 : isTablet ? 6 : 8,
      }}
    >
      {/* Course Title */}
      <Typography
        variant={isMobile ? 'h5' : 'h3'}
        component="h1"
        sx={{
          fontWeight: 'bold',
          mb: 4,
          color: '#000000',
          fontSize: isMobile ? '1.6rem' : '3rem', // Adjust title size for mobile
        }}
      >
        {course.title}
      </Typography>

      {/* Show error message if any */}
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Grid container spacing={isMobile ? 2 : 4}>
        {/* Left Section */}
        <Grid item xs={12} md={8}>
          {/* Course Description */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="body1"
              sx={{
                mb: 4,
                fontSize: isMobile ? '14px' : '18px',
                lineHeight: '1.7',
                color: '#333',
              }}
            >
              {course.description}
            </Typography>
          </Box>


          {/* Apply URL Clicked Count - Changed Section */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
              Job Applications in Progress
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '16px' }}>
              Currently, <strong>{applyUrlClickedCount}</strong> {applyUrlClickedCount === 1 ? 'candidate is' : 'candidates are'} actively applying to job opportunities by clicking the "Apply" button on the platform.
            </Typography>
          </Box>

          {/* Course registeration Section */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
              Course Registration Interest
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '16px' }}>
              {applyUrlClickedCount} {applyUrlClickedCount === 1 ? 'person has' : 'people have'} shown interest in registering for this course.
            </Typography>
          </Box>

          {/* Learning Objectives */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#000000' }}>
              Learning Objectives
            </Typography>
            <List>
              {course.learningObjectives?.map((objective, index) => (
                <ListItem key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleOutlineIcon sx={{ color: '#00bcd4', mr: 1 }} />
                  <ListItemText primary={objective} sx={{ fontSize: '16px', color: '#555' }} />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Target Audience */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="body1" sx={{ mb: 4, fontSize: '16px', color: '#555' }}>
              <strong>Target Audience:</strong> {course.targetAudience}
            </Typography>
          </Box>

          {/* Instructor Info */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#000000' }}>
              Instructor
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '16px', color: '#444' }}>
              <strong>{course.instructor.name}</strong> - {course.instructor.bio}
            </Typography>
          </Box>

          {/* Curriculum */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#000000' }}>
              Course Curriculum
            </Typography>
            <List>
              {course.curriculum?.map((lesson, index) => (
                <ListItem key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleOutlineIcon sx={{ color: '#00bcd4', mr: 1 }} />
                  <ListItemText primary={lesson.title} sx={{ fontSize: '16px', color: '#555' }} />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Reviews */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#000000' }}>
              Student Reviews
            </Typography>
            {course.reviews?.map((review, index) => (
              <Box key={index} sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
                <StarIcon sx={{ color: '#ffc107', mr: 1 }} />
                <Typography variant="body2" sx={{ color: '#555', fontSize: '16px' }}>
                  "{review.comment}" - <strong>{review.name}</strong>
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>

        {/* Right Section - Pricing & Enrollment */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              backgroundColor: '#f5f5f5',
              p: isMobile ? 2 : 4, // Adjust padding for mobile
              borderRadius: '8px',
              position: 'sticky',
              top: 20,
              boxShadow: 2,
              transition: 'all 0.3s ease',
              '&:hover': { boxShadow: 6 },
            }}
          >
            <Typography variant="h6" sx={{ mb: 4, fontWeight: 'bold', color: '#000000' }}>
              Price: ₹{course.price}
            </Typography>

            {isAlreadyEnrolled ? (
              <>
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  sx={{
                    padding: isMobile ? '10px' : '12px', // Adjust padding for mobile
                    fontSize: isMobile ? '16px' : '18px',
                    fontWeight: 'bold',
                    backgroundColor: '#4caf50',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#388e3c',
                    },
                  }}
                  onClick={handleOpenCourse}
                >
                  Open Course
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  disabled
                  sx={{
                    mt: 2,
                    padding: isMobile ? '10px' : '12px',
                    fontSize: isMobile ? '16px' : '18px',
                    fontWeight: 'bold',
                    backgroundColor: '#ccc',
                    color: '#666',
                  }}
                >
                  Enrolled
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={isEnrolling}
                sx={{
                  padding: isMobile ? '10px' : '12px',
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: 'bold',
                  backgroundColor: '#000',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#fff',
                    color: '#000',
                    border: '1px solid #000',
                  },
                }}
                onClick={handleEnroll}
              >
                {isEnrolling ? <CircularProgress size={24} color="inherit" /> : 'Enroll Now'}
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CourseDetailsPage;

